import { useState } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import EmotionDetectorFooter from '@/components/emotion-detector/EmotionDetectorFooter';
import EmotionDetectorHeader from '@/components/emotion-detector/EmotionDetectorHeader';
import messages from '@/components/emotion-detector/messages';
import { useJournalEntries } from '@/hooks/emotion-detector';

import DeleteConfirmDialog from './components/DeleteConfirmDialog';
import EmptyState from './components/EmptyState';
import ErrorState from './components/ErrorState';
import JournalEntryCard from './components/JournalEntryCard';
import LoadingState from './components/LoadingState';
import { EntriesGrid, PageContainer, Title } from './styles';

export default function LogScreen() {
	const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

	const { formatMessage } = useIntl();
	const { entries, notes, handleNoteChange, deleteEntry, deletingId, loading, error } = useJournalEntries();

	const handleDeleteClick = (entryId: string) => {
		setDeleteConfirmId(entryId);
	};

	const handleDeleteConfirm = async () => {
		if (deleteConfirmId) {
			try {
				await deleteEntry(deleteConfirmId);
				setDeleteConfirmId(null);
			} catch {
				// Error already handled in hook
			}
		}
	};

	const handleDeleteCancel = () => {
		setDeleteConfirmId(null);
	};

	const showEntries = !loading && !error && entries.length > 0;
	const showEmpty = !loading && !error && entries.length === 0;

	return (
		<>
			<EmotionDetectorHeader currentPage="log" />
			<PageContainer>
				<Title>
					<Text size={FontSizes['2xl']} weight={FontWeights.Medium}>
						{formatMessage(messages.mainTitle)}
					</Text>
				</Title>

				{loading && <LoadingState />}
				{error && <ErrorState error={error} />}
				{showEmpty && <EmptyState />}

				{showEntries && (
					<EntriesGrid>
						{entries.map(entry => (
							<JournalEntryCard
								key={entry.id}
								entry={entry}
								notes={notes}
								onNoteChange={handleNoteChange}
								onDeleteClick={handleDeleteClick}
								isDeleting={deletingId === entry.id}
							/>
						))}
					</EntriesGrid>
				)}
			</PageContainer>

			{deleteConfirmId && (
				<DeleteConfirmDialog onConfirm={handleDeleteConfirm} onCancel={handleDeleteCancel} />
			)}
			<EmotionDetectorFooter />
		</>
	);
}
